import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { FileText, Loader2, X } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const CERT_TYPES = [
  { value: "bonafide", label: "Bonafide Certificate" },
  { value: "lc", label: "Leaving Certificate" },
  { value: "tc", label: "Transfer Certificate" },
  { value: "transcript", label: "Academic Transcript" },
];

export default function Certificates() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ student_name: "", student_email: "", cert_type: "bonafide", purpose: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchCerts(); }, []);

  const fetchCerts = async () => {
    try {
      const { data: d } = await axios.get(`${API}/certificates`, { withCredentials: true });
      setData(d);
    } catch {} finally { setLoading(false); }
  };

  const handleIssue = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await axios.post(`${API}/certificates`, form, { withCredentials: true });
      setShowModal(false); fetchCerts();
    } catch {} finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="certificates-module">
      <div className="flex items-start justify-between border-b border-[#D8D4CB] pb-5">
        <div>
          <p className="font-['Syne'] text-[10px] font-bold uppercase tracking-[0.3em] text-[#BDBAA8] mb-1">Academic Records</p>
          <h2 className="font-['Syne'] font-bold text-2xl text-[#0C0C10]">Certificate Generation</h2>
        </div>
        <button onClick={() => setShowModal(true)} data-testid="issue-cert-btn"
          className="bg-[#0C0C10] text-[#EAFF00] font-['Syne'] font-bold text-[10px] uppercase tracking-widest px-5 py-3 hover:bg-[#1C1C22] transition-colors flex items-center gap-2">
          <FileText className="w-3.5 h-3.5" /> Issue Certificate
        </button>
      </div>

      {/* Cert type cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {CERT_TYPES.map((t, i) => (
          <div key={t.value} className="bg-white border border-[#E2DDD4] p-5 cursor-pointer hover:border-[#0C0C10] transition-colors" onClick={() => { setForm(p => ({...p, cert_type: t.value})); setShowModal(true); }}>
            <div className="w-8 h-8 bg-[#0C0C10] flex items-center justify-center mb-3"><FileText className="w-4 h-4 text-[#EAFF00]" /></div>
            <p className="font-['Syne'] font-bold text-xs text-[#0C0C10]">{t.label}</p>
            <p className="font-['DM_Sans'] text-[10px] text-[#BDBAA8] mt-1">{data.filter(d => d.cert_type === t.value).length} issued</p>
          </div>
        ))}
      </div>

      <div className="bg-white border border-[#E2DDD4]">
        <div className="grid gap-0 px-5 py-3 border-b border-[#E2DDD4] bg-[#FAFAF7]" style={{ gridTemplateColumns: "1.5fr 1fr 1fr 120px 80px" }}>
          {["Student", "Certificate", "Purpose", "Cert No", "Status"].map(h => (
            <span key={h} className="font-['Syne'] text-[9px] font-bold uppercase tracking-[0.25em] text-[#BDBAA8]">{h}</span>
          ))}
        </div>
        {loading ? <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-[#BDBAA8]" /></div>
          : data.map((item, i) => (
          <div key={item._id} className="grid gap-0 px-5 py-3.5 border-b border-[#F0ECE4] last:border-0 hover:bg-[#FAFAF7] items-center" style={{ gridTemplateColumns: "1.5fr 1fr 1fr 120px 80px" }}>
            <div>
              <p className="font-['Syne'] font-bold text-xs text-[#0C0C10]">{item.student_name}</p>
              <p className="font-['DM_Sans'] text-[10px] text-[#BDBAA8]">{item.student_email}</p>
            </div>
            <span className="font-['Syne'] font-bold text-xs text-[#0C0C10] uppercase">{CERT_TYPES.find(t => t.value === item.cert_type)?.label || item.cert_type}</span>
            <span className="font-['DM_Sans'] text-xs text-[#7A7468]">{item.purpose || "—"}</span>
            <span className="font-['JetBrains_Mono'] text-[10px] text-[#BDBAA8]">{item.cert_no}</span>
            <span className="font-['Syne'] font-bold text-[9px] uppercase px-2 py-1 border border-[#22C55E]/30 text-[#16A34A] bg-[#22C55E]/8">{item.status}</span>
            <button onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/pdf/certificate/${item._id}`, "_blank")} data-testid={`cert-pdf-${item._id}`}
              className="font-['Syne'] font-bold text-[9px] uppercase tracking-wider text-[#0A7AFF] border border-[#0A7AFF]/20 px-2.5 py-1.5 hover:bg-[#0A7AFF]/8 transition-colors">
              PDF
            </button>
          </div>
        ))}
      </div>

      <AnimatePresence>
        {showModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-[#F5F2EA] max-w-md w-full p-8">
              <div className="flex justify-between mb-6">
                <h3 className="font-['Syne'] font-bold text-lg text-[#0C0C10]">Issue Certificate</h3>
                <button onClick={() => setShowModal(false)}><X className="w-5 h-5 text-[#BDBAA8]" /></button>
              </div>
              <form onSubmit={handleIssue} className="space-y-5">
                {[["student_name", "Student Name"], ["student_email", "Student Email"], ["purpose", "Purpose"]].map(([f, l]) => (
                  <div key={f}><label className="block font-['Syne'] text-[9px] font-bold uppercase tracking-[0.2em] text-[#7A7468] mb-2">{l}</label>
                  <input value={form[f]} onChange={e => setForm(p => ({...p, [f]: e.target.value}))} required={f !== "purpose"}
                    className="w-full bg-transparent border-0 border-b border-[#D8D4CB] pb-2 text-sm font-['DM_Sans'] text-[#0C0C10] focus:outline-none focus:border-[#0C0C10] transition-colors" /></div>
                ))}
                <div><label className="block font-['Syne'] text-[9px] font-bold uppercase tracking-[0.2em] text-[#7A7468] mb-2">Certificate Type</label>
                <select value={form.cert_type} onChange={e => setForm(p => ({...p, cert_type: e.target.value}))}
                  className="w-full bg-transparent border-0 border-b border-[#D8D4CB] pb-2 text-sm font-['DM_Sans'] text-[#0C0C10] focus:outline-none">
                  {CERT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select></div>
                <button type="submit" disabled={saving} className="w-full bg-[#0C0C10] text-[#EAFF00] font-['Syne'] font-bold text-xs uppercase tracking-widest py-3.5 hover:bg-[#1C1C22] transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Generate Certificate"}
                </button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
